import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { DemoSection } from "../components/DemoSection";
import {
  LayoutGrid, Truck, Archive, FileUp, Printer, History,
  ArrowRight, Boxes, ShieldCheck, Ruler, Star, WifiOff, Download,
  Layers, CheckCircle2,
} from "lucide-react";

const SHOWCASE_BG =
  "https://images.unsplash.com/photo-1609867271967-a82f85c48531?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BLUEPRINT_IMG =
  "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const GRID_BG = {
  backgroundImage:
    "linear-gradient(#1C2026 1px, transparent 1px), linear-gradient(90deg, #1C2026 1px, transparent 1px)",
  backgroundSize: "44px 44px",
};

// Panels for the animated "auto-pack" mini yard (percentages of the table box)
const PANELS = [
  { id: "W-101", mpa: "40", x: 3, y: 6, w: 40, h: 26, c: "#2560C9", d: 0 },
  { id: "W-102", mpa: "40", x: 45, y: 6, w: 40, h: 26, c: "#3B75DF", d: -0.7 },
  { id: "C-210", mpa: "32", x: 3, y: 37, w: 36, h: 26, c: "#F59E0B", d: -1.4 },
  { id: "C-211", mpa: "32", x: 41, y: 37, w: 44, h: 26, c: "#2560C9", d: -2.1 },
  { id: "B-330", mpa: "50", x: 3, y: 68, w: 48, h: 26, c: "#3B75DF", d: -2.8 },
  { id: "B-331", mpa: "50", x: 53, y: 68, w: 32, h: 26, c: "#10B981", d: -3.5 },
];

const FEATURES = [
  { icon: LayoutGrid, title: "Yard Planner", span: "lg:col-span-4",
    desc: "Scaled casting tables you can drag into any layout. Drop, rotate and auto-pack panels into tidy rows — grouped by concrete strength, with overlap warnings.", visual: "yard" },
  { icon: Truck, title: "Load Planner", span: "lg:col-span-2",
    desc: "Book concrete by the load with a hard 6.8 m³ cap and one-click auto-pack by MPa.", visual: "load" },
  { icon: FileUp, title: "PDF Shop-Drawing Import", span: "lg:col-span-2",
    desc: "Drop in shop drawings — we read the title block automatically.", visual: "terminal" },
  { icon: Archive, title: "Drawing Bank", span: "lg:col-span-2",
    desc: "A persistent library of every panel, grouped by job. Star priorities to float them up.", visual: "image" },
  { icon: History, title: "Multi-Yard & History", span: "lg:col-span-2",
    desc: "Run several yards in parallel, each with isolated day-by-day history, autosave & undo.", visual: "none" },
];

const STEPS = [
  { n: "01", icon: FileUp, title: "Import your drawings", desc: "Bulk-upload PDFs into the Drawing Bank. Panels are parsed and grouped by job automatically." },
  { n: "02", icon: Boxes, title: "Pack the yard & loads", desc: "Auto-pack panels onto the casting tables and into concrete loads — or drag them exactly where you want." },
  { n: "03", icon: Printer, title: "Print the day", desc: "Hand the crew a scaled schedule showing every panel, table and load for the shift." },
];

const STATS = [
  { v: "6.8 m³", l: "Per-load cap enforced" },
  { v: "100%", l: "Offline-capable (PWA)" },
  { v: "1-click", l: "Yard & load auto-pack" },
  { v: "PDF", l: "Auto dimension capture" },
];

export default function Home() {
  const { user } = useAuth();
  const primaryTo = user ? "/app" : "/register";
  const primaryLabel = user ? "Open Yard Planner" : "Get Started";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <style>{`
        @keyframes ez-fadeup { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform: translateY(0); } }
        @keyframes ez-pack { 0% { opacity:0; transform: translateX(-24px) scale(.96); } 12% { opacity:1; transform: translateX(0) scale(1); } 86% { opacity:1; transform: translateX(0) scale(1); } 100% { opacity:0; transform: translateX(-24px) scale(.96); } }
        @keyframes ez-fill { 0% { width:0%; } 55% { width:93%; } 100% { width:93%; } }
        @keyframes ez-blink { 0%,49% { opacity:1; } 50%,100% { opacity:0; } }
        .ez-up { opacity:0; animation: ez-fadeup .7s cubic-bezier(.2,.7,.2,1) forwards; }
        @media (prefers-reduced-motion: reduce) { .ez-up{animation:none;opacity:1;} .ez-pack{animation:none!important;opacity:1!important;} }
      `}</style>

      {/* Nav */}
      <header data-testid="nav-header" className="fixed top-0 inset-x-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#2E3033]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 h-16">
          <Logo className="h-9" />
          <nav className="flex items-center gap-1 sm:gap-2">
            <a href="#features" className="hidden md:block text-[11px] font-mono uppercase tracking-[0.15em] text-[#A0AABF] hover:text-white px-3 py-2 transition-colors">Features</a>
            <a href="#demo" className="hidden md:block text-[11px] font-mono uppercase tracking-[0.15em] text-[#A0AABF] hover:text-white px-3 py-2 transition-colors">See it work</a>
            <a href="#how" className="hidden md:block text-[11px] font-mono uppercase tracking-[0.15em] text-[#A0AABF] hover:text-white px-3 py-2 transition-colors">How it works</a>
            {user ? (
              <Link data-testid="home-open-app" to="/app" className="flex items-center gap-1.5 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-sm px-4 py-2 transition-colors">
                Open Planner <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                <Link data-testid="home-signin" to="/login" className="text-[#A0AABF] hover:text-white text-sm px-4 py-2 transition-colors">Sign In</Link>
                <Link data-testid="home-getstarted" to="/register" className="bg-[#2560C9] hover:bg-[#3B75DF] text-white text-sm px-4 py-2 transition-colors">Get Started</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section data-testid="hero-section" className="relative pt-16 border-b border-[#2E3033]">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        <div className="absolute -top-20 -left-32 w-[36rem] h-[36rem] bg-[#2560C9]/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center py-16 lg:py-24">
          <div className="lg:col-span-6">
            <span className="ez-up inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B75DF] border border-[#2560C9]/40 px-3 py-1.5" style={{ animationDelay: "0s" }}>
              <span className="w-1.5 h-1.5 bg-[#2560C9]" /> Precast Production Control
            </span>
            <h1 className="ez-up font-heading font-black tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl mt-6" style={{ animationDelay: ".08s" }}>
              PRECISION IN
              <span className="block text-[#3B75DF]">EVERY POUR.</span>
            </h1>
            <p className="ez-up mt-6 text-base sm:text-lg text-[#A0AABF] max-w-xl leading-relaxed" style={{ animationDelay: ".16s" }}>
              From shop drawing to casting bed to concrete truck — organise every panel, table and pour in one command centre.
              Import PDFs, auto-pack the yard, and print a schedule the crew can actually follow. Even offline.
            </p>
            <div className="ez-up mt-9 flex flex-wrap items-center gap-3" style={{ animationDelay: ".24s" }}>
              <Link data-testid="home-primary-cta" to={primaryTo} className="group flex items-center gap-2 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-sm font-medium px-6 py-3 transition-colors">
                {primaryLabel}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              {!user && (
                <Link data-testid="home-secondary-cta" to="/login" className="border border-[#2E3033] hover:border-[#2560C9] text-white text-sm px-6 py-3 transition-colors">Sign In</Link>
              )}
            </div>
            <div className="ez-up mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[11px] font-mono text-[#6E7787]" style={{ animationDelay: ".32s" }}>
              <span className="flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5 text-[#3B75DF]" /> Auto dimension capture</span>
              <span className="flex items-center gap-1.5"><WifiOff className="w-3.5 h-3.5 text-[#F59E0B]" /> Works offline on-site</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" /> Secure accounts</span>
            </div>
          </div>

          {/* Animated mini yard */}
          <div className="lg:col-span-6 ez-up" style={{ animationDelay: ".2s" }}>
            <MiniYard />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-[#2E3033] bg-[#0E1012]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-[#2E3033]">
          {STATS.map((s) => (
            <div key={s.l} className="px-5 py-8 text-center">
              <div className="font-heading font-black text-2xl sm:text-3xl text-white">{s.v}</div>
              <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" data-testid="features-bento" className="max-w-7xl mx-auto px-5 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B75DF]">// What's inside</span>
          <h2 className="mt-3 font-heading font-black text-3xl sm:text-5xl tracking-tight">Everything the yard needs. Nothing it doesn't.</h2>
        </div>
        <div className="grid lg:grid-cols-6 gap-4">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`${f.span} group bg-[#16181A] border border-[#2E3033] hover:border-[#2560C9]/60 p-7 transition-colors flex flex-col`}
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#2560C9]/12 border border-[#2560C9]/30 text-[#3B75DF] group-hover:bg-[#2560C9] group-hover:text-white transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">{f.title}</h3>
              </div>
              <p className="mt-4 text-sm text-[#A0AABF] leading-relaxed">{f.desc}</p>
              <div className="mt-auto pt-5">
                {f.visual === "yard" && <YardMini />}
                {f.visual === "load" && <LoadMini />}
                {f.visual === "terminal" && <TerminalMini />}
                {f.visual === "image" && (
                  <div className="h-28 overflow-hidden border border-[#2E3033]">
                    <img src={BLUEPRINT_IMG} alt="Shop drawing" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity grayscale group-hover:grayscale-0" />
                  </div>
                )}
                {f.visual === "none" && (
                  <div className="flex items-center gap-2 text-[11px] font-mono text-[#6E7787]">
                    <Layers className="w-4 h-4 text-[#3B75DF]" /> autosave · undo/redo · per-yard history
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* See it work — interactive demo */}
      <DemoSection />

      {/* Offline / PWA showcase */}
      <section data-testid="offline-showcase" className="relative border-y border-[#2E3033]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${SHOWCASE_BG})` }} />
        <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        <div className="absolute inset-0" style={{ ...GRID_BG, opacity: 0.4 }} />
        <div className="relative max-w-7xl mx-auto px-5 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B75DF]">// Built for the field</span>
            <h2 className="mt-3 font-heading font-black text-3xl sm:text-5xl tracking-tight leading-tight">Works on-site.<br />Even off-grid.</h2>
            <p className="mt-5 text-[#A0AABF] max-w-lg leading-relaxed">
              Signal drops in the yard? Keep planning. EAZIKAST is a full PWA — edit panels, loads and layouts offline,
              and everything auto-syncs the moment you reconnect. Install it to a phone or tablet and run it like a native app.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <span className="flex items-center gap-2 border border-[#2560C9]/50 text-[#3B75DF] text-xs font-mono uppercase tracking-wider px-3 py-2">
                <Download className="w-4 h-4" /> Install to home screen
              </span>
              <span className="flex items-center gap-2 border border-[#10B981]/40 text-[#10B981] text-xs font-mono uppercase tracking-wider px-3 py-2">
                <CheckCircle2 className="w-4 h-4" /> Auto-sync on reconnect
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { v: "Offline", l: "Full edit access, no signal needed" },
              { v: "Multi-yard", l: "Separate yards, isolated history" },
              { v: "Conflict-safe", l: "Warns & resolves on sync" },
              { v: "Installable", l: "Runs like a native app" },
            ].map((c) => (
              <div key={c.l} className="bg-[#16181A]/90 border border-[#2E3033] p-5">
                <div className="font-heading font-black text-xl text-white">{c.v}</div>
                <div className="mt-1.5 text-[11px] font-mono text-[#6E7787] leading-relaxed">{c.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-[#0E1012]">
        <div className="max-w-7xl mx-auto px-5 py-20 md:py-28">
          <div className="max-w-2xl mb-12">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#3B75DF]">// Three steps</span>
            <h2 className="mt-3 font-heading font-black text-3xl sm:text-5xl tracking-tight">From drawing to pour day.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div key={s.n} className="relative bg-[#16181A] border border-[#2E3033] hover:border-[#2560C9]/60 p-7 transition-colors">
                <span className="font-heading font-black text-6xl text-[#22252A] absolute top-3 right-5 select-none">{s.n}</span>
                <div className="w-11 h-11 flex items-center justify-center bg-[#2560C9]/12 border border-[#2560C9]/30 text-[#3B75DF]">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-[#A0AABF] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28">
        <div className="relative overflow-hidden border border-[#2560C9]/40 bg-[#0E1012] p-10 md:p-16">
          <div className="absolute inset-0" style={GRID_BG} />
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#2560C9]/20 blur-3xl rounded-full" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tight">Ready to organise the yard?</h2>
            <p className="mt-4 text-[#A0AABF] max-w-lg">Start planning your next casting day in minutes. No spreadsheets, no guesswork.</p>
            <Link data-testid="home-cta-bottom" to={primaryTo} className="group inline-flex items-center gap-2 mt-8 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-sm font-medium px-7 py-3 transition-colors">
              {primaryLabel}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="border-t border-[#2E3033] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 pt-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-8">
            <Logo className="h-8" />
            <div className="flex items-center gap-5 text-[11px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#how" className="hover:text-white transition-colors">How it works</a>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to={user ? "/app" : "/register"} className="hover:text-white transition-colors">{user ? "Open App" : "Get Started"}</Link>
            </div>
          </div>
          <div className="font-heading font-black tracking-tighter text-[#141619] leading-none select-none text-[22vw] lg:text-[18rem] -mb-[3vw] pointer-events-none">
            EAZIKAST
          </div>
        </div>
        <div className="border-t border-[#2E3033]">
          <p className="max-w-7xl mx-auto px-5 py-5 text-[11px] font-mono text-[#6E7787]">© {new Date().getFullYear()} EAZIKAST — Yard Organizer</p>
        </div>
      </footer>
    </div>
  );
}

/* ---- Hero animated mini yard ---- */
function MiniYard() {
  return (
    <div className="relative bg-[#0E1012] border border-[#2E3033] shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2E3033]">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">TABLE A · 40.0 × 9.0 m</span>
        <span className="flex items-center gap-1.5 text-[10px] font-mono text-[#10B981]"><span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse" /> AUTO-PACK</span>
      </div>
      <div className="relative m-3" style={{ height: 300 }}>
        <div className="absolute inset-0 bg-[#0A0A0A] border border-[#2E3033]" style={{ backgroundImage: "linear-gradient(#1C2026 1px,transparent 1px),linear-gradient(90deg,#1C2026 1px,transparent 1px)", backgroundSize: "20px 20px" }} />
        {PANELS.map((p) => (
          <div
            key={p.id}
            className="absolute flex flex-col items-center justify-center overflow-hidden ez-pack"
            style={{
              left: `${p.x}%`, top: `${p.y}%`, width: `${p.w}%`, height: `${p.h}%`,
              backgroundColor: `${p.c}dd`, border: `1px solid ${p.c}`,
              animation: "ez-pack 8s ease-in-out infinite", animationDelay: `${p.d}s`,
            }}
          >
            <span className="text-[10px] font-mono font-bold text-white leading-none">{p.id}</span>
            <span className="text-[8px] font-mono text-white/80 leading-none mt-0.5">{p.mpa} MPa</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#2E3033] text-[10px] font-mono">
        <span className="text-[#6E7787]">6 PANELS · 3 STRENGTHS</span>
        <span className="text-[#3B75DF]">GROUPED BY MPa</span>
      </div>
    </div>
  );
}

/* ---- Bento visuals ---- */
function YardMini() {
  return (
    <div className="relative h-28 bg-[#0A0A0A] border border-[#2E3033] overflow-hidden" style={{ backgroundImage: "linear-gradient(#1C2026 1px,transparent 1px),linear-gradient(90deg,#1C2026 1px,transparent 1px)", backgroundSize: "16px 16px" }}>
      {[
        { x: 4, y: 12, w: 30, h: 30, c: "#2560C9" },
        { x: 38, y: 12, w: 24, h: 30, c: "#3B75DF" },
        { x: 4, y: 52, w: 40, h: 34, c: "#F59E0B" },
        { x: 66, y: 12, w: 20, h: 74, c: "#10B981" },
        { x: 48, y: 52, w: 14, h: 34, c: "#2560C9" },
      ].map((b, i) => (
        <div key={i} className="absolute" style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%`, backgroundColor: `${b.c}cc`, border: `1px solid ${b.c}` }} />
      ))}
    </div>
  );
}

function LoadMini() {
  const rows = [{ n: "LOAD 01", p: 93, ok: true }, { n: "LOAD 02", p: 68, ok: true }, { n: "LOAD 03", p: 100, ok: false }];
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r.n}>
          <div className="flex items-center justify-between text-[10px] font-mono mb-1">
            <span className="text-[#A0AABF]">{r.n}</span>
            <span style={{ color: r.ok ? "#10B981" : "#FF3B30" }}>{(6.8 * r.p / 100).toFixed(1)} / 6.8 m³</span>
          </div>
          <div className="h-2 bg-[#0A0A0A] border border-[#2E3033] overflow-hidden">
            <div style={{ backgroundColor: r.ok ? "#2560C9" : "#FF3B30", height: "100%", animation: "ez-fill 3s ease-out infinite", animationDelay: `${i * 0.3}s`, width: `${r.p}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TerminalMini() {
  return (
    <div className="bg-[#0A0A0A] border border-[#2E3033] p-3 font-mono text-[10px] leading-relaxed">
      <div className="text-[#6E7787]">$ parse WALL-101.pdf</div>
      <div className="text-[#A0AABF]">id <span className="text-white">W-101</span> · t <span className="text-white">150mm</span></div>
      <div className="text-[#A0AABF]">area <span className="text-white">14.4m²</span> · vol <span className="text-white">2.16m³</span></div>
      <div className="text-[#A0AABF]">fc <span className="text-[#3B75DF]">40 MPa</span> · mass <span className="text-white">5.18t</span></div>
      <div className="text-[#10B981]">✓ captured<span style={{ animation: "ez-blink 1s step-end infinite" }}>_</span></div>
    </div>
  );
}
